import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Form,
  Spin,
  Typography,
  Segmented,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import I18n from 'i18n-js';
import Dragger from 'antd/es/upload/Dragger';
import useErrors from 'hooks/forms/useErrors';
import dayjs from 'dayjs';
import { ticket_path, tickets_path } from 'js-routes/generated/routes';
import {
  shape, arrayOf, string, func, number, bool,
} from 'prop-types';
import useDebounceAdminUserFetch from './hooks/useDebounceAdminUserFetch';
import useDebounceAssigneeUserFetch from './hooks/useDebounceAssigneeUserFetch';
import useSubmitForm from './hooks/useSubmitForm';
import DetailedInfo from './DetailedInfo';
import PriorityLabel from './nested/PriorityLabel';
import StatusLabel from './nested/StatusLabel';
import CwoModuleLabel from './nested/CwoModuleLabel';
import cwoModuleConfig from './helpers/cwoModuleConfig';
import UserWithAvatarLabel from './nested/UserWithAvatarLabel';
import TicketTypeLabel from './nested/TicketTypeLabel';

const SMALL_DEVICE_WIDTH = 950;

const { TextArea } = Input;
const { Text } = Typography;

const TicketModalForm = ({
  priorities = [],
  statuses = [],
  ticketTypes = [],
  selectedNode = {
    id: null,
    title: '',
    status: '',
    priority: '',
    ticketType: '',
    assignee: {
      id: null,
      type: '',
      username: '',
      avatarUrl: '',
    },
    responsibleUser: {
      id: null,
      username: '',
      avatarUrl: '',
    },
    author: {
      id: null,
      type: '',
      username: '',
      avatarUrl: '',
    },
    dueDate: '',
    createdAt: '',
    description: '',
    cwoModule: '',
    companyId: null,
  },
  setSelectedNode = (_node) => {},
  currentUserInfo = {
    id: null,
    type: '',
    username: '',
    avatarUrl: '',
    companyId: null,
  },
  setInitialTickets = (_tickets) => {},
  isModalOpen = false,
  setIsModalOpen = (_open) => {},
  newTicketModal = false,
  ticketFromDashboard = false,
  responsibleUserInfo = {
    id: null,
    type: '',
    username: '',
    avatarUrl: '',
  },
}) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(window.innerWidth < SMALL_DEVICE_WIDTH);

  const resolveFormUrl = () => {
    if (selectedNode?.id) {
      return ticket_path(selectedNode.id);
    }
    return tickets_path();
  };

  const {
    handleError,
    clearErrors,
    errorStatus,
    errorMessage,
  } = useErrors();

  const {
    isFormSending,
    handleSubmitForm,
    submitButtonName,
  } = useSubmitForm({
    form,
    formUrl: resolveFormUrl(),
    clearErrors,
    handleError,
    id: selectedNode?.id,
    setIsModalOpen,
    setInitialTickets,
    ticketFromDashboard,
  });

  const {
    adminUserFetch,
    dataFetching,
    adminUsers,
  } = useDebounceAdminUserFetch({ initialOptions: [] });

  const {
    assigneeUserFetch,
    assigneeFetching,
    assigneeUsers,
  } = useDebounceAssigneeUserFetch({ initialOptions: [] });

  const userLabel = (username, avatarUrl) => (
    <UserWithAvatarLabel
      username={username}
      avatarUrl={avatarUrl}
    />
  );

  useEffect(() => {
    if (selectedNode?.id) {
      setIsModalOpen(true);
      /* eslint-disable camelcase */
      form.setFieldsValue({
        title: selectedNode.title,
        description: selectedNode.description,
        priority: selectedNode.priority,
        status: selectedNode.status,
        ticket_type: selectedNode.ticketType,
        author_id: selectedNode.author
          ? {
            value: selectedNode.author.id,
            label: userLabel(selectedNode.author.username, selectedNode.author.avatarUrl),
          }
          : null,
        author_type: selectedNode.author
          ? selectedNode.author.type
          : '',
        assignee_id: selectedNode.assignee
          ? {
            value: selectedNode.assignee.id,
            label: userLabel(selectedNode.assignee.username, selectedNode.assignee.avatarUrl),
          }
          : null,
        assignee_type: selectedNode.assignee
          ? selectedNode.assignee.type
          : '',
        responsible_user_id: selectedNode.responsibleUser
          ? {
            value: selectedNode.responsibleUser.id,
            label: userLabel(
              selectedNode.responsibleUser.username,
              selectedNode.responsibleUser.avatarUrl,
            ),
          }
          : null,
        due_date: selectedNode.dueDate
          ? dayjs(selectedNode.dueDate)
          : null,
        cwo_module: selectedNode.cwoModule || '',
        company_id: selectedNode.companyId,
      });
      /* eslint-enable camelcase */
    }
  }, [form, selectedNode, setIsModalOpen]);

  useEffect(() => {
    if (newTicketModal === true) {
      form.resetFields();
      setSelectedNode(null);
      setIsModalOpen(true);
    }
  }, [form, newTicketModal, setIsModalOpen, setSelectedNode]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < SMALL_DEVICE_WIDTH);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedNode(null);
    form.resetFields();
  };

  const resolveModalTitle = () => {
    if (selectedNode?.id) {
      return I18n.t('tickets.form.edit_ticket', { id: selectedNode.id });
    }
    return I18n.t('tickets.form.new_ticket');
  };

  const statusOptions = statuses.map((status) => ({
    value: status,
    label: <StatusLabel status={status} />,
  }));

  const priorityOptions = priorities.map((priority) => ({
    value: priority,
    label: <PriorityLabel priority={priority} />,
  }));

  const cwoModuleOptions = Object.keys(cwoModuleConfig).map((cwoModule) => ({
    value: cwoModule,
    label: <CwoModuleLabel cwoModule={cwoModule} />,
  }));

  const ticketTypeOptions = ticketTypes.map((ticketType) => ({
    value: ticketType,
    label: <TicketTypeLabel type={ticketType} />,
  }));

  const canUserEdit = selectedNode?.id ? (selectedNode?.author.id === currentUserInfo?.id) : true;

  return (
    <Modal
      title={resolveModalTitle()}
      open={isModalOpen}
      onCancel={handleModalClose}
      footer={null}
      width={900}
      cancelText={I18n.t('helpers.submit.ticket.cancel')}
    >
      <Form
          /* eslint-disable camelcase */
        form={form}
        layout="vertical"
        onFinish={handleSubmitForm}
        initialValues={{
          priority: 'medium',
          status: 'open',
          author_type: currentUserInfo.type,
          assignee_type: responsibleUserInfo.type,
          author_id: {
            value: currentUserInfo.id,
            label: userLabel(currentUserInfo.username, currentUserInfo.avatarUrl),
          },
          responsible_user_id: {
            value: responsibleUserInfo.id,
            label: userLabel(responsibleUserInfo.username, responsibleUserInfo.avatarUrl),
          },
          due_date: dayjs().add(1, 'week'),
          cwo_module: 'other',
          assignee_id: {
            value: responsibleUserInfo.id,
            label: userLabel(responsibleUserInfo.username, responsibleUserInfo.avatarUrl),
          },
          company_id: currentUserInfo.companyId,
          ticket_type: 'task',
          /* eslint-enable camelcase */
        }}
      >
        <Row gutter={24}>
          <Col xs={24} md={16} lg={16}>
            <div className="tickets-index-page__tickets-form__left-column">
              <Form.Item name="company_id" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.ticket_type')}
                name="ticket_type"
                style={{ marginBottom: 12, gap: '10px' }}
              >
                {isMobile ? (
                  <Select
                    options={ticketTypeOptions}
                    disabled={!canUserEdit}
                  />
                ) : (
                  <Segmented
                    disabled={!canUserEdit}
                    options={ticketTypeOptions}
                  />
                )}
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.title')}
                name="title"
                rules={[{ required: true, message: I18n.t('tickets.form.title_required') }]}
                validateStatus={errorStatus('title')}
                help={errorMessage('title')}
              >
                <Input disabled={!canUserEdit} placeholder={I18n.t('tickets.form.title_hint')} />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.description')}
                name="description"
                rules={[{ required: true, message: I18n.t('tickets.form.description_required') }]}
                validateStatus={errorStatus('description')}
                help={errorMessage('description')}
                className="tickets-index-page__form-item"
              >
                <TextArea
                  rows={6}
                  placeholder={I18n.t('tickets.form.description_hint')}
                  disabled={!canUserEdit}
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('tickets.form.attachments')}
                name="files"
                valuePropName="fileList"
                validateStatus={errorStatus('files')}
                help={errorMessage('files')}
                className="tickets-index-page__form-item"
              >
                <Dragger
                  multiple
                  disabled
                  beforeUpload={() => false}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {I18n.t('tickets.form.attachments_hint')}
                  </p>
                  <p className="ant-upload-hint">
                    {I18n.t('tickets.form.attachments_hint_multiple')}
                  </p>
                </Dragger>
              </Form.Item>

              {selectedNode?.id && (
              <>
                <hr className="tickets-index-page__horizontal-line" />
                <DetailedInfo
                  ticketId={selectedNode?.id}
                  currentUserInfo={currentUserInfo}
                />
              </>
              )}

            </div>
          </Col>

          <Col xs={24} md={8} lg={8}>
            <Form.Item name="author_type" hidden>
              <Input type="hidden" />
            </Form.Item>

            <Form.Item
              label={I18n.t('activerecord.attributes.ticket.author')}
              name="author_id"
              validateStatus={errorStatus('author')}
              help={errorMessage('author')}
              className="tickets-index-page__form-item"
            >
              <Select
                labelInValue
                placeholder={I18n.t('search_with_min_input', { count: 3 })}
                showSearch
                filterOption={false}
                autoClearSearchValue={false}
                onSearch={adminUserFetch}
                notFoundContent={dataFetching ? <Spin size="small" /> : null}
                options={adminUsers}
                loading={dataFetching}
                fieldNames={{ label: 'username', value: 'user_id' }}
                disabled
              />
            </Form.Item>

            <Form.Item
              label={I18n.t('activerecord.attributes.ticket.responsible_user')}
              name="responsible_user_id"
              validateStatus={errorStatus('responsible_user_id')}
              help={errorMessage('responsible_user_id')}
              className="tickets-index-page__form-item"
            >
              <Select
                labelInValue
                placeholder={I18n.t('search_with_min_input', { count: 3 })}
                showSearch
                filterOption={false}
                autoClearSearchValue={false}
                onSearch={adminUserFetch}
                notFoundContent={dataFetching ? <Spin size="small" /> : null}
                options={adminUsers}
                loading={dataFetching}
                fieldNames={{ label: 'username', value: 'admin_user_id' }}
                disabled
              />
            </Form.Item>

            <Form.Item name="assignee_type" hidden>
              <Input type="hidden" />
            </Form.Item>

            <Form.Item
              label={I18n.t('activerecord.attributes.ticket.assignee')}
              name="assignee_id"
              validateStatus={errorStatus('assignee_id')}
              help={errorMessage('assignee_id')}
              className="tickets-index-page__form-item"
            >
              <Select
                labelInValue
                optionLabelProp="label"
                placeholder={I18n.t('search_with_min_input', { count: 3 })}
                showSearch
                filterOption={false}
                autoClearSearchValue={false}
                onSearch={assigneeUserFetch}
                notFoundContent={assigneeFetching ? <Spin size="small" /> : null}
                loading={assigneeFetching}
                onChange={(_value, option) => {
                  const selectedOption = Array.isArray(option) ? option[0] : option;

                  form.setFieldsValue({
                    // eslint-disable-next-line camelcase
                    assignee_type: selectedOption?.user_type,
                  });
                }}
              >
                {assigneeUsers.map((user) => (
                  <Select.Option
                    key={user.key}
                    value={user.id}
                    user_type={user.user_type}
                    label={(
                      <UserWithAvatarLabel
                        username={user.username}
                        avatarUrl={user.avatarUrl}
                      />
                    )}
                  >
                    <UserWithAvatarLabel username={user.username} avatarUrl={user.avatarUrl} />
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={I18n.t('activerecord.attributes.ticket.priority')}
              name="priority"
              validateStatus={errorStatus('priority')}
              help={errorMessage('priority')}
              className="tickets-index-page__form-item"
            >
              <Select
                options={priorityOptions}
                disabled={!canUserEdit}
              />
            </Form.Item>

            <Form.Item
              label={I18n.t('activerecord.attributes.ticket.status')}
              name="status"
              validateStatus={errorStatus('status')}
              help={errorMessage('status')}
              className="tickets-index-page__form-item"
            >
              <Select
                options={statusOptions}
              />
            </Form.Item>

            <Form.Item
              label={I18n.t('activerecord.attributes.ticket.cwo_module')}
              name="cwo_module"
              validateStatus={errorStatus('cwo_module')}
              help={errorMessage('cwo_module')}
              className="tickets-index-page__form-item"
            >
              <Select
                options={cwoModuleOptions}
              />
            </Form.Item>

            <Form.Item
              label={I18n.t('activerecord.attributes.ticket.due_date')}
              name="due_date"
              validateStatus={errorStatus('due_date')}
              help={errorMessage('due_date')}
              className="tickets-index-page__form-item"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="LL"
                disabled={!canUserEdit}
              />
            </Form.Item>

            {selectedNode?.id && (
            <Text
              type="secondary"
              className="tickets-index-page__form-created-at"
            >
              {dayjs(selectedNode.createdAt).format('LL HH:mm')}
            </Text>
            )}

            <div className="tickets-index-page__ticket-form-submit-btn">
              <Button
                type="primary"
                loading={isFormSending}
                onClick={() => form.submit()}
              >
                {submitButtonName}
              </Button>
            </div>

          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

TicketModalForm.propTypes = {
  priorities: arrayOf(string),
  statuses: arrayOf(string),
  ticketTypes: arrayOf(string),
  selectedNode: shape({
    id: number,
    title: string,
    status: string,
    priority: string,
    ticketType: string,
    assignee: shape({
      id: number,
      type: string,
      username: string,
      avatarUrl: string,
    }),
    responsibleUser: shape({
      id: number,
      username: string,
      avatarUrl: string,
    }),
    author: shape({
      id: number,
      type: string,
      username: string,
      avatarUrl: string,
    }),
    dueDate: string,
    description: string,
    cwoModule: string,
  }),
  setSelectedNode: func,
  currentUserInfo: shape({
    id: number,
    type: string,
    username: string,
    avatarUrl: string,
  }),
  setInitialTickets: func,
  isModalOpen: bool,
  setIsModalOpen: func,
  newTicketModal: bool,
  ticketFromDashboard: bool,
  responsibleUserInfo: shape({
    id: number,
    type: string,
    username: string,
    avatarUrl: string,
  }),
};

export default TicketModalForm;
